<template>
  <div v-if="lock" class="alert alert-warning work-order-lock-banner" role="alert">
    <div class="work-order-lock-banner__text">
      <i class="fa fa-lock"></i>
      <strong>{{ translate('work_order_locked') }}</strong>
      <span>
        {{ lock.IsMine ? translate('work_order_locked_by_me') : lockedByText }}
      </span>
      <span v-if="lockedAtText" class="work-order-lock-banner__since">{{ lockedAtText }}</span>
      <span>
        {{ translate('work_order_locked_blocks_changes') }}
        <template v-if="!lock.IsMine">{{ translate('work_order_locked_only_owner') }}</template>
      </span>
    </div>

    <!-- Kilidi yalnız sahibi kaldırabilir; başkasına düğme göstermek, basınca 409 alan bir
         düğme göstermek olurdu. Kararı sunucu veriyor (`IsMine`). -->
    <button
      v-if="lock.IsMine"
      type="button"
      class="btn btn-warning btn-sm work-order-lock-banner__release"
      :disabled="releasing"
      @click="confirmRelease"
    >
      <i class="fa fa-unlock"></i>
      {{ translate('release_work_order_lock') }}
    </button>
  </div>
</template>

<script>
import * as dateFormat from '../../helpers/dateFormat'
import * as swal from '../../helpers/swal'
import { getWorkOrderLock, releaseWorkOrderLock } from '../../scripts/services/workOrderLocks'

/**
 * Bir iş emri saha uygulamasında kilitliyse gösterilen şerit.
 *
 * **Neden var.** Kilit, teknisyene "bu iş emri sende, çevrimdışı çalışabilirsin" sözü veriyor;
 * kilitliyken web'den yazmak sunucuda engelleniyor. Bunu yalnız kaydederken söylemek,
 * kullanıcıya önce iş yaptırıp sonra reddetmek olurdu — şerit engeli önden söylüyor.
 *
 * **Kilidi kaldırma da burada.** Telefonunu sahada bırakan teknisyen kilidi web'den açabilmeli
 * (ürün kararı, 2026-09-10). Bedeli var: o cihazda gönderilmemiş değişiklik varsa siliniyor,
 * bu yüzden onay ekranı bunu açıkça yazıyor.
 */
export default {
  name: 'WorkOrderLockBanner',
  props: {
    workOrderId: { type: [Number, String], required: true },
  },
  data() {
    return {
      lock: null,
      releasing: false,
    }
  },
  computed: {
    lockedByText() {
      // Ad okunamamışsa (kullanıcı silinmiş olabilir) kilidin varlığını yine söylüyoruz.
      if (!this.lock || !this.lock.LockedByName) return this.translate('work_order_locked')
      return this.translate('work_order_locked_by', { name: this.lock.LockedByName })
    },
    lockedAtText() {
      if (!this.lock || !this.lock.LockedAt) return null
      return this.translate('work_order_locked_since', {
        date: dateFormat.viewDateTimeFormat(this.lock.LockedAt),
      })
    },
  },
  watch: {
    workOrderId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    async load() {
      this.lock = await getWorkOrderLock(this.workOrderId)
      this.$emit('lock-changed', this.lock)
    },

    async confirmRelease() {
      const self = this
      const result = await swal.check(
        self,
        self.translate('release_work_order_lock_confirm'),
        self.translate('release_work_order_lock_warning'),
      )
      if (!result.value) return

      self.releasing = true
      try {
        await releaseWorkOrderLock(self.workOrderId)
        toastr.success(self.translate('release_work_order_lock_success'))
        // Tazeden okunuyor: kilit gerçekten kalktı mı, cevabı sunucu versin.
        await self.load()
      } catch (error) {
        // Sunucu başkasının kilidini 409 ile reddediyor; mesajı olduğu gibi gösteriyoruz.
        const message = (error && error.body) || (error && error.message)
        toastr.error(typeof message === 'string' ? message : self.translate('error'))
      } finally {
        self.releasing = false
      }
    },
  },
}
</script>

<style scoped>
.work-order-lock-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.work-order-lock-banner__text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.work-order-lock-banner__since {
  opacity: 0.8;
}

.work-order-lock-banner__release {
  white-space: nowrap;
}
</style>
